import {
  startSubscription,
  updateSubscription,
  updateSubscriptionPoNumber,
} from "src/billinglatest/clients/service.client";
import type {
  BillingCycle,
  CurrencyCode,
  PlanId,
} from "../PlanBillingStep/constants";
import type { PixiAddress } from "../../hooks/usePixiWizardState";
import { pixiAddressToBillingAddress } from "./mappers/checkoutPayload";

/**
 * The subset of the purchase order needed to set the plan and activate the
 * subscription (steps 3–5 of the Pixi purchase). Mirrors the relevant fields of
 * `PurchaseInput`.
 *
 * `currency` is optional so the no-fallback currency guard still applies here:
 * Chargebee locks the currency to the customer, so if it's missing we fail
 * rather than charge a currency the customer never agreed to.
 */
export interface PixiPurchaseOrder {
  /** Plan tier group, e.g. "core" | "pro". */
  planGroup: PlanId;
  /** Billing cycle, "year" | "month". */
  billingCycle: BillingCycle;
  /** The trialist's locked billing currency, resolved upstream. No fallback. */
  currency?: CurrencyCode;
  licenses: number;
  /**
   * Set for the free-device deal: activates the subscription as a PIXI bundle
   * (adds the free device + paid shipping). The backend resolves the shipping
   * cost from its rate table using the address country + service.
   */
  bundle?: {
    shippingAddress: PixiAddress;
    shippingService: string;
  };
  /**
   * Optional PO number applied to invoices (GRWBKK-1704). Unvalidated, so it is
   * applied best-effort AFTER the subscription is active and never fails the
   * purchase. Carried on the order so both the inline and the redirect-resume
   * paths (whose only shared step is this module) apply it identically.
   */
  poNumber?: string;
}

/**
 * The user-facing default text thrown when the billing currency is missing.
 * Kept identical to usePurchase's `pixi_wizard.checkout.error.currency_missing`
 * default. Callers that need the translated string may pass
 * `opts.currencyMissingMessage`; otherwise this default (matching the existing
 * copy) is used. This keeps `finalizePurchase` a pure, React-free module while
 * preserving the exact user-facing error text.
 */
export const CURRENCY_MISSING_MESSAGE =
  "We couldn't confirm your billing currency. Please try again.";

export interface FinalizePurchaseOptions {
  /** Stripe PaymentIntent id from the 3DS/SCA charge challenge (card only). */
  paymentIntentId?: string | null;
  /** Space id scoping the billing SDK calls. */
  spaceId?: string;
  /** Optional translated override for the currency-missing error text. */
  currencyMissingMessage?: string;
}

/**
 * Steps 3–6 of the Pixi purchase, extracted verbatim as a pure async function:
 *
 * Step 3 — currency guard (no fallback) → build the plan id.
 * Step 4 — set the chosen plan + licences (updateSubscription).
 * Step 5 — activate the trial → paid subscription (startSubscription). For the
 * free-device deal this passes the PIXI bundle option + shipping destination.
 * Step 6 — apply the optional PO number (best-effort; never fails the purchase).
 *
 * `paymentIntentId` is resolved by the caller (card 3DS/SCA); this module does
 * not touch Stripe.
 */
export const finalizePurchase = async (
  order: PixiPurchaseOrder,
  opts: FinalizePurchaseOptions = {},
): Promise<void> => {
  const { paymentIntentId, spaceId, currencyMissingMessage } = opts;

  // 3. Build the plan id from the trialist's locked currency (resolved
  // upstream). No fallback: fail rather than charge a currency the customer
  // never agreed to. group/cycle are the user's choice; only currency is
  // constrained by Chargebee.
  if (!order.currency) {
    throw new Error(currencyMissingMessage ?? CURRENCY_MISSING_MESSAGE);
  }
  const planId = `${order.planGroup}-${order.billingCycle}-${order.currency.toLowerCase()}`;

  // 4. Set the chosen plan + licences (3DS/SCA paymentIntentId for card).
  await updateSubscription(
    {
      planId,
      quantity: order.licenses,
      paymentIntentId: paymentIntentId ?? null,
    },
    spaceId,
  );

  // 5. Activate the trial → paid subscription. For the free-device deal, pass
  // the PIXI bundle option + shipping so the backend adds the device and
  // charges the shipping it resolves from its rate table.
  const startRequest: Parameters<typeof startSubscription>[0] = order.bundle
    ? {
        option: "pixi",
        shippingAddress: pixiAddressToBillingAddress(
          order.bundle.shippingAddress,
        ),
        shippingService: order.bundle.shippingService,
      }
    : undefined;
  await startSubscription(startRequest, spaceId);

  // 6. Apply the optional PO number to the now-active subscription. This is an
  // unvalidated invoice reference, so it runs AFTER the purchase has fully
  // succeeded and is best-effort: a failure here must never report the
  // (already-charged, already-activated) purchase as failed. Swallow it.
  if (order.poNumber?.trim()) {
    try {
      await updateSubscriptionPoNumber(
        { poNumber: order.poNumber.trim() },
        spaceId,
      );
    } catch {
      // Non-fatal: the purchase is complete; the PO can be set later.
    }
  }
};
